import React, { Fragment } from "react";
import clsx from "clsx";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
} from "recharts";

import {
  Metric,
  Trend,
  selectFormat,
  StandardTooltip,
  SOCIALS_COLORS,
} from "./../Utils/Utils.js";
import Heading from "../Elements/Heading.js";

const PrimeSeriesSocialSummary = (props) => {
  const data = props.data.cells;

  const renderHeader = (data) => (
    <>
      <Heading size={5} className={"mb-2"}>
        {data.label}
      </Heading>

      <div className="d-flex mb-2">
        <div className="mr-2">
          <Metric size="lg" type={data.type} value={data.total.value} />
        </div>
      </div>
    </>
  );

  const renderCharts = (data, type) =>
    data.map((chart, index) => {
      return (
        <Fragment key={index}>
          <div className="d-flex flex-row align-items-center">
            <span className="trend__label text-capitalize mr-1 ml-0">
              {chart.name}
            </span>

            <Metric size="xs" type={type} {...chart.total} />
          </div>
          {chart.data && (
            <ResponsiveContainer height={35}>
              <BarChart
                data={chart.data}
                layout="vertical"
                width={100}
                height={15}
                margin={{ top: 0, right: 0, left: 0, bottom: 10 }}
                barCategoryGap={0}
              >
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = chart.data;
                        if (payload !== null) {
                          // because the payload always return 1 element.. we get it
                          const tooltipData = payload[0];
                          // we create the tooltips based on the keys of the object `tooltipData`, however, we ignore the "total" attribute
                          // get the attributes/values in an array of key/value pairs
                          let tooltips = Object.entries(tooltipData)
                            // defines which attribute should be removed from the list
                            .filter((t) => t[0] !== "total")
                            // map the key/value pair as tooltip (eg. [ 'organic', 41682376 ])
                            .map((t, idx) => (
                              <div className="standard-tooltip__item" key={idx}>
                                <span className="standard-tooltip__key">
                                  <span
                                    className="standard-tooltip__icon"
                                    style={{
                                      // get the background color according the index of the field
                                      background:
                                        SOCIALS_COLORS[chart.name][idx],
                                    }}
                                  />
                                  {/* key */}
                                  <span
                                    className={clsx(
                                      "standard-tooltip__key",
                                      "text-capitalize"
                                    )}
                                  >
                                    {t[0]}
                                  </span>
                                </span>
                                <span className="standard-tooltip__value">
                                  {/* value */}
                                  {selectFormat(type, t[1])}
                                </span>
                              </div>
                            ));

                          return (
                            <div className="standard-tooltip">
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header text-capitalize">
                                  {chart.name}
                                </span>
                              </header>
                              <main className="standard-tooltip__main">
                                {tooltips}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
                <XAxis type="number" hide />
                <YAxis type="category" hide />
                {/* {renderBars} */}
                <Bar
                  stackId="stacked"
                  dataKey={
                    chart.data[0].organic
                      ? "organic"
                      : chart.data[0].feed
                      ? "feed"
                      : "value"
                  }
                  fill={SOCIALS_COLORS[chart.name][0]}
                />
                {chart.data[0].organic ||
                  (chart.data[0].stories && (
                    <Bar
                      stackId="stacked"
                      dataKey={
                        chart.data[0].organic
                          ? "paid"
                          : chart.data[0].stories
                          ? "stories"
                          : "total"
                      }
                      fill={
                        chart.data[0].organic || chart.data[0].stories
                          ? SOCIALS_COLORS[chart.name][1]
                          : "#fff"
                      }
                    />
                  ))}

                {/* we should always have the total that defines the same proportion for all other charts
                that are not "connected" */}
                <Bar stackId="stacked" dataKey={"total"} fill={"#fff"} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Fragment>
      );
    });

  const renderBenchmarks = (data, type) => (
    <div>
      <Heading size={6} className={"mb-2 mt-2"}>
        vs. Benchmarks
      </Heading>
      <div className="d-flex flex-column mt-1">
        {data.map((d, i) => (
          <Trend
            {...d}
            key={i}
            highlight={true}
            className="mb-1"
            label={
              <div>
                <div className="trend__name"> {d.name}</div>
                <span className="trend__value">
                  ({selectFormat(type, d.value).toUpperCase()})
                </span>
              </div>
            }
          />
        ))}
      </div>
    </div>
  );

  const MetricRow = ({ label, metricData, metricType }) => {
    if (!metricData) {
      return null; // Explicitly return null if metricData is not defined
    }

    return (
      <div className="row d-flex flex-row align-items-baseline">
        <div className="col-3 d-flex justify-content-end">
          <Metric size="xs" type={metricType} {...metricData} />
        </div>
        <div className="col-9">
          <span className="text-muted pl-1">{label}</span>
        </div>
      </div>
    );
  };

  const renderHashtags = (data) => (
    <div className="row mt-4 justify-content">
      {data.map((item) => (
        <div
          className="col-6 col-xs-6 col-sm-4 col-md-4 col-lg-3 col-xl-3 mb-4"
          key={item.id || item.hashtag}
        >
          {item.hashtag && (
            <Heading size={5} className="mb-2 pl-3 font-weight-bold">
              {item.hashtag}
            </Heading>
          )}
          <MetricRow
            label="Views"
            metricData={item.views}
            metricType="number"
          />
          <MetricRow
            label="Impressions"
            metricData={item.impressions}
            metricType="number"
          />
          <MetricRow
            label="Video View Rate"
            metricData={item.view_rate}
            metricType="percentage"
          />
          <MetricRow
            label="Engagements"
            metricData={item.engagements}
            metricType="number"
          />
          <MetricRow
            label="Actions"
            metricData={item.actions}
            metricType="number"
          />
          <MetricRow
            label="Engagement Rate"
            metricData={item.engagement_rate}
            metricType="percentage"
          />
        </div>
      ))}
    </div>
  );

  const renderSection = (widgets) => {
    const columns = widgets.length >= 4 ? "col-6  col-lg-3" : "col-4";

    return (
      <div className="row prime-series-social__separator">
        {widgets.map((data, i) => {
          return (
            <div
              className={`${columns} prime-series-social__summary-widget`}
              key={i}
            >
              {data.label !== "Hashtags" && renderHeader(data)}

              {data.charts && renderCharts(data.charts, data.type)}
              {data.benchmarks &&
                data.benchmarks.length > 0 &&
                renderBenchmarks(data.benchmarks, data.type)}
              <hr className="social-performance__hr--divider" />
            </div>
          );
        })}
      </div>
    );
  };

  const renderSections = () => {
    const cells = data.filter((cell) => cell.label !== "Hashtags");
    let first_row = [];
    let second_row = [];

    if (cells.length <= 4) {
      first_row = cells;
    } else {
      first_row = cells.splice(0, Math.ceil(cells.length / 2));
      second_row = cells;
    }

    const hashtags = data.find((cell) => cell.label === "Hashtags").rows;

    return (
      <section>
        {renderSection(first_row)}
        {second_row.length > 0 && (
          <>
            <hr className="prime-series-social__hr" />
            {renderSection(second_row)}
          </>
        )}
        <hr className="prime-series-social__hr" />
        {renderHashtags(hashtags)}
      </section>
    );
  };

  return renderSections();
};
export default PrimeSeriesSocialSummary;
